import React, { JSX } from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    ReferenceManyField,
    ReferenceField,
    useRecordContext,
    WithRecord,
    Link,
    Datagrid,
    SelectField,
    ShowButton,
    FunctionField
} from 'react-admin';
import {
    Grid,
    LinearProgress,
} from '@mui/material';

import { useStripeModeFetch } from "../global-hooks/default-system-hooks";
import { SubscriptionInvoice, SubscriptionInvoiceItem, SubscriptionRefund } from "../../types/entities";
import { convertCurrencyToSymbol, invoiceTypes } from "../../defaults/defaults";
import { SubscriptionInvoiceShowPreview } from './components/SubscriptionInvoiceShowPreview';
import { SubscriptionInvoiceRefundBlock } from "./components/SubscriptionInvoiceRefundBlock";
import { RefundStatusChip } from "./components/RefundStatusChip";
import { formatRefundAmount, notesPreview } from "./components/refund-format";

const ShowInvoiceTitle: () => JSX.Element = (): JSX.Element => {
    const record: any = useRecordContext();
    if (!record) return null;
    return <span>{ "Invoice #" + record.id }</span>
}

export const SubscriptionInvoiceShow: () => JSX.Element = (): JSX.Element => {
    const { isTestMode, loadingTestModeData } = useStripeModeFetch();

    return(
        <Show actions={ false } title={ <ShowInvoiceTitle /> }>
            <Grid container spacing={ 2 }>
                <Grid size={ 6 }>
                    <SimpleShowLayout>
                        <TextField source="id" />
                        <WithRecord render={
                            (record: SubscriptionInvoice): JSX.Element => {
                                let colour: string = 'orangered';
                                switch (record.status) {
                                    case 'paid':
                                        colour = 'green';
                                        break;
                                    case 'uncollectable':
                                    case "void":
                                        colour = 'red';
                                        break;
                                }
                                return (
                                    <span style={ { color: colour, textTransform: 'uppercase' } } >{ record.status }</span>
                                );
                            }
                        } label="Status" />
                        <WithRecord render={
                            (record: SubscriptionInvoice): JSX.Element => {
                                return (
                                    <span >{ convertCurrencyToSymbol(record.currency) + parseFloat(record.amountDue).toFixed(2) }</span>
                                );
                            }
                        } label="Amount Due" />
                        <WithRecord render={
                            (record: SubscriptionInvoice): JSX.Element => {
                                return (
                                    <span >{ convertCurrencyToSymbol(record.currency) + parseFloat(record.amountPaid).toFixed(2) }</span>
                                );
                            }
                        } label="Amount Paid" />
                        <ReferenceField source="customerAccount" reference="customer_accounts" >
                            <TextField source="id"/>
                        </ReferenceField>
                        <TextField source="invoiceId" label={ "Invoice ID" } />
                        <SelectField source="invoiceType" choices={ invoiceTypes } />
                        <WithRecord render={
                            (record: SubscriptionInvoice): JSX.Element => {
                                return (
                                    <>
                                        { (loadingTestModeData) &&
                                            <LinearProgress sx={ { margin: '10px 0', width: '75%' } } />
                                        }
                                        { (!loadingTestModeData) &&
                                            <>
                                                { (isTestMode) &&
                                                    <Link to={ "https://dashboard.stripe.com/acct_1S676LIYyPSARt7l/test/invoices/" + record.stripeId }
                                                          target="_blank"
                                                    >{ record.stripeId }
                                                    </Link>
                                                }
                                                {(!isTestMode) &&
                                                    <Link to={ "https://dashboard.stripe.com/acct_1S676LIYyPSARt7l/invoices/" + record.stripeId }
                                                          target="_blank"
                                                    >{ record.stripeId }
                                                    </Link>
                                                }
                                            </>
                                        }
                                    </>
                                );
                            }
                        } label="Stripe ID" />
                        <DateField source="createdAt" showTime />
                        <DateField source="updatedAt" showTime />
                        <ReferenceManyField
                            label="Invoice Items"
                            source="subscriptionInvoiceItems"
                            target="id"
                            reference="subscription_invoice_items"
                        >
                            <Datagrid bulkActionButtons={ false }>
                                <TextField source="id" />
                                <TextField source="name" />
                                <WithRecord render={
                                    (record: SubscriptionInvoiceItem): JSX.Element => {
                                        return (
                                            <span >{ convertCurrencyToSymbol(record.currency) + parseFloat(record.amount).toFixed(2) }</span>
                                        );
                                    }
                                } label="Amount Paid*" />
                                <TextField source="quantity" label="Seats/Quantity" />
                            </Datagrid>
                        </ReferenceManyField>
                        <span style={ { margin: '10px 0' } } >{ "*Amount Paid excludes any discounts" }</span>
                        <ReferenceManyField
                            label="Refunds"
                            source="subscriptionRefunds"
                            target="id"
                            reference="subscription_refunds"
                        >
                            <Datagrid bulkActionButtons={ false }>
                                <TextField source="id" />
                                <FunctionField label="Status"
                                               render={ (record: SubscriptionRefund): JSX.Element => <RefundStatusChip status={ record.status } dense /> } />
                                <FunctionField label="Amount Refunded"
                                               render={ (record: SubscriptionRefund): string => formatRefundAmount(record.currency, record.amount) } />
                                <FunctionField label="Notes"
                                               render={ (record: SubscriptionRefund): string => notesPreview(record.notes, 60) } />
                                <DateField source="createdAt" label="Refunded at" showTime />
                                <ShowButton label="View" />
                            </Datagrid>
                        </ReferenceManyField>
                    </SimpleShowLayout>
                </Grid>
                <Grid size={ 6 } sx={ { padding: '20px' } }>
                    <SubscriptionInvoiceShowPreview actions={ <SubscriptionInvoiceRefundBlock /> } />
                </Grid>
            </Grid>
        </Show>
    );
}